import * as React from 'react';
import {Suspense, useState} from 'react';
import {graphql, useLazyLoadQuery, useMutation} from 'react-relay';
import {useParams} from 'react-router-dom';
import {BadgeCheck} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {FacebookLoginForm} from '../components/AuthMenu';
import {TalentFacebookPageQuery} from './__generated__/TalentFacebookPageQuery.graphql';
import {TalentFacebookPageMutation} from './__generated__/TalentFacebookPageMutation.graphql';

const QUERY = graphql`
    query TalentFacebookPageQuery {
        viewer {
            id
            name
            facebookPagesKnown
            facebookPages { id name }
            talentProfile {
                id
                name
                facebookProfileUrl
                facebookPageUrl
                facebookPageName
                facebookPageVerified
            }
        }
        facebookLoginEnabled
    }`;

const CONNECT = graphql`
    mutation TalentFacebookPageMutation($input: ConnectTalentFacebookInput!) {
        connectTalentFacebook(input: $input) {
            talentProfile { id name facebookProfileUrl facebookPageUrl facebookPageName facebookPageVerified }
            userErrors { message }
        }
    }`;

const inputClass = 'w-full bg-black border border-gray-800 px-3 py-2 text-sm text-white focus:border-lumina-accent outline-none';

// Crew land here from the connect link staff send them (/talent/connect/:token), sign in
// with Facebook, and choose which of their Pages (and which profile) the site links to.
// Once connected they can come back to /talent/facebook to change it.
const ConnectForm: React.FC<{ token?: string }> = ({token}) => {
    const data = useLazyLoadQuery<TalentFacebookPageQuery>(QUERY, {}, {fetchPolicy: 'network-only'});
    const [connect, connecting] = useMutation<TalentFacebookPageMutation>(CONNECT);
    const profile = data.viewer?.talentProfile;
    const [pageId, setPageId] = useState<string>(() => {
        const current = data.viewer?.facebookPages.find((p) => profile?.facebookPageVerified && profile.facebookPageUrl?.endsWith(`/${p.id}`));
        return current?.id ?? '';
    });
    const [profileUrl, setProfileUrl] = useState(profile?.facebookProfileUrl ?? '');
    const [errors, setErrors] = useState<string[]>([]);
    const [saved, setSaved] = useState<string | null>(null);

    if (!data.viewer) {
        return (
            <div className="max-w-md mx-auto text-center border border-gray-800 bg-[#050505] p-10">
                <p className="text-gray-400 text-sm mb-6">Sign in with Facebook to connect it to your Love Wins talent profile.</p>
                {data.facebookLoginEnabled ? (
                    <FacebookLoginForm className="w-full py-3 bg-[#1877F2] text-white font-bold text-sm tracking-widest uppercase hover:bg-[#1877F2]/80">
                        Continue with Facebook
                    </FacebookLoginForm>
                ) : (
                    <p className="text-gray-500 text-xs">Facebook sign-in isn't available right now.</p>
                )}
            </div>
        );
    }

    if (!token && !profile) {
        return <p className="text-center text-gray-400 text-sm">Your sign-in isn't connected to a talent profile yet. Ask us for a connect link.</p>;
    }

    const pages = data.viewer.facebookPages;
    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        setErrors([]);
        connect({
            variables: {input: {token: token ?? null, facebookPageId: pageId, facebookProfileUrl: profileUrl.trim()}},
            onCompleted: (res) => {
                const p = res.connectTalentFacebook;
                if (p?.talentProfile) setSaved(p.talentProfile.name);
                else setErrors(p?.userErrors.map((u) => u.message) ?? ['Something went wrong']);
            },
            onError: (err) => setErrors([err.message]),
        });
    };

    if (saved) {
        return (
            <div className="max-w-md mx-auto text-center border border-gray-800 bg-[#050505] p-10">
                <BadgeCheck className="w-8 h-8 text-lumina-accent mx-auto mb-4"/>
                <p className="text-gray-300 text-sm">Facebook is connected to {saved}'s profile.</p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="max-w-md mx-auto border border-gray-800 bg-[#050505] p-8 space-y-5">
            <p className="text-gray-400 text-sm">
                Signed in as <span className="text-white">{data.viewer.name}</span>
                {profile ? <> · connected to <span className="text-white">{profile.name}</span></> : null}.
            </p>

            <label className="block">
                <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Professional Page</span>
                {pages.length > 0 ? (
                    <select value={pageId} onChange={(e) => setPageId(e.target.value)} className={inputClass}>
                        <option value="">No Page</option>
                        {pages.map((p) => <option key={p.id} value={p.id}>{p.name ?? p.id}</option>)}
                    </select>
                ) : (
                    <span className="block text-xs text-gray-500">
                        {data.viewer.facebookPagesKnown
                            ? "Facebook didn't list any Pages you manage."
                            : "We can't see your Pages from Facebook yet, so we'll add your Page link for you."}
                        {profile?.facebookPageUrl && <> Currently linked: <a href={profile.facebookPageUrl} target="_blank" rel="noreferrer" className="text-lumina-accent">{profile.facebookPageName ?? profile.facebookPageUrl}</a></>}
                    </span>
                )}
            </label>

            <label className="block">
                <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Personal profile link (optional)</span>
                <input value={profileUrl} onChange={(e) => setProfileUrl(e.target.value)} placeholder="https://www.facebook.com/your.name" className={inputClass}/>
            </label>

            {errors.length > 0 && <ul className="text-sm text-red-400">{errors.map((m) => <li key={m}>{m}</li>)}</ul>}

            <button type="submit" disabled={connecting} className="w-full py-3 bg-[#1877F2] text-white font-bold text-sm tracking-widest uppercase hover:bg-[#1877F2]/80 disabled:opacity-50">
                {profile && !token ? 'Save' : 'Connect'}
            </button>
        </form>
    );
};

const TalentFacebookPage: React.FC = () => {
    const {token} = useParams();
    return (
        <div className="bg-lumina-dark min-h-screen text-white">
            <Navbar/>
            <main className="max-w-7xl mx-auto px-6 pt-28 pb-24">
                <div className="text-center mb-10">
                    <span className="text-lumina-purple font-display text-sm tracking-wider uppercase">Talent</span>
                    <h1 className="text-4xl md:text-5xl font-display font-bold mt-2">CONNECT <span className="text-gray-600">FACEBOOK</span></h1>
                </div>
                <Suspense fallback={<p className="text-gray-500 text-sm text-center">Loading…</p>}>
                    <ConnectForm token={token}/>
                </Suspense>
            </main>
            <Footer/>
        </div>
    );
};

export default TalentFacebookPage;
